import React, { useCallback, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { isOfflineMode } from '@atlaskit/editor-common/connectivity/isOfflineMode';
import {
	getAriaKeyshortcuts,
	insertElements,
	ToolTipContent,
} from '@atlaskit/editor-common/keymaps';
import { toolbarInsertBlockMessages as messages } from '@atlaskit/editor-common/messages/insert-block';
import Popup from '@atlaskit/editor-common/Popup';
import { useMenuPopupSizing } from '@atlaskit/editor-common/quick-insert/use-menu-popup-sizing';
import { useEditorToolbar } from '@atlaskit/editor-common/toolbar/context';
import { TOOLBAR_BUTTON_TEST_ID } from '@atlaskit/editor-common/toolbar/keys';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { akEditorMenuZIndex } from '@atlaskit/editor-shared-styles/constants';
import { AddIcon } from '@atlaskit/editor-toolbar/add-icon';
import { ToolbarButton } from '@atlaskit/editor-toolbar/toolbar-button';
import { ToolbarTooltip } from '@atlaskit/editor-toolbar/toolbar-tooltip';
import { useToolbarUI } from '@atlaskit/editor-toolbar/ui-context';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { InsertBlockPlugin } from '../../insertBlockPluginType';
import { RegisteredInsertMenuContent } from './RegisteredInsertMenuContent';

const DEFAULT_MENU_MAX_HEIGHT = 520;
const POPUP_OFFSET: [number, number] = [0, 3];

type Props = {
	api?: ExtractInjectionAPI<InsertBlockPlugin>;
};

/**
 * The toolbar owns its popup lifecycle, but renders the same registered items
 * and menu model as slash command.
 */
export const RegisteredInsertButton = ({ api }: Props): React.JSX.Element | null => {
	const { editorView } = useEditorToolbar();
	const isOptimistic = isExperimentEnabled('platform_editor_ssr_toolbar_optimistic');
	const { formatMessage } = useIntl();
	const { isDisabled, popupsBoundariesElement, popupsMountPoint, popupsScrollableElement } =
		useToolbarUI();
	const [isOpen, setIsOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const menuHeight = useMenuPopupSizing({
		target: buttonRef.current,
		boundariesElement: popupsBoundariesElement,
		scrollableElement: popupsScrollableElement,
		maxHeight: DEFAULT_MENU_MAX_HEIGHT,
		offset: POPUP_OFFSET[1],
		isOpen,
	});
	const { connectivityMode, isAllowed } = useSharedPluginStateWithSelector(
		api,
		['connectivity', 'typeAhead'],
		(states) => ({
			connectivityMode: states.connectivityState?.mode,
			isAllowed: states.typeAheadState?.isAllowed,
		}),
	);

	const close = useCallback((restoreFocus = false) => {
		setIsOpen(false);
		if (restoreFocus) {
			requestAnimationFrame(() => buttonRef.current?.focus());
		}
	}, []);
	const open = useCallback(() => {
		// Early clicks must not queue a popup to open when the editor becomes ready.
		if (!editorView) {
			return;
		}
		setIsOpen(true);
	}, [editorView]);
	const onPopupUnmount = useCallback(() => {
		// Outside dismissal must not move focus away from the element that was clicked.
		setIsOpen(false);
	}, []);
	const closeAndRestoreFocus = useCallback(() => close(true), [close]);
	const closeForSelection = useCallback(() => close(false), [close]);

	if (!api?.insertBlock || (!editorView && !isOptimistic)) {
		return null;
	}

	return (
		<>
			<ToolbarTooltip
				content={
					<ToolTipContent
						description={formatMessage(messages.insertMenu)}
						keymap={insertElements}
					/>
				}
			>
				<ToolbarButton
					ariaKeyshortcuts={getAriaKeyshortcuts(insertElements)}
					iconBefore={<AddIcon size="small" label={formatMessage(messages.insertMenu)} />}
					// Keep the optimistic SSR button visible before the live view is attached.
					isDisabled={isDisabled || ((!isOptimistic || !!editorView) && !isAllowed)}
					isSelected={isOpen}
					onClick={isOpen ? () => close(true) : open}
					ref={buttonRef}
					testId={TOOLBAR_BUTTON_TEST_ID.INSERT}
				/>
			</ToolbarTooltip>
			{isOpen && buttonRef.current && editorView && (
				<Popup
					alignX="right"
					boundariesElement={popupsBoundariesElement}
					fitHeight={menuHeight}
					fitWidth={350}
					mountTo={popupsMountPoint}
					offset={POPUP_OFFSET}
					onUnmount={onPopupUnmount}
					preventOverflow
					scrollableElement={popupsScrollableElement}
					target={buttonRef.current}
					zIndex={akEditorMenuZIndex}
				>
					<RegisteredInsertMenuContent
						api={api}
						maxHeight={menuHeight}
						editorView={editorView}
						isOffline={isOfflineMode(connectivityMode)}
						onClose={closeAndRestoreFocus}
						onDismiss={closeForSelection}
						onSelect={closeForSelection}
						target={buttonRef.current}
					/>
				</Popup>
			)}
		</>
	);
};
